import * as React from "react";
import { Alert, AlertTitle, Box, Chip, Stack, Typography } from "@mui/material";
import { WarningIcon } from "@phosphor-icons/react";

import { usePriceBook } from "@/hooks/api/use-campaign-requests";

import { marketLabel, PRICE_CODE_LABELS } from "../constants";
import { labelOf } from "../humanise";
import { buildCarrierRows, buildChargeRows, type RateRow } from "./rate-rows";

/**
 * The rates that are stopping quotes, wherever someone who can fix them is looking.
 *
 * A rate nobody has agreed is not a price book detail — `priceRequest()` refuses to quote against
 * one *whose note it recognises*, and the platform defaults are what every account falls back to,
 * so one unconfirmed platform rate blocks every quote for every client. The rates whose note says
 * the same thing in wording the server does not match are worse and are named first: nothing
 * blocks, so they are quoted. A fresh database ships with ten of them
 * (UX §16 note 2: "not set" is shown in amber, not hidden, because "the UI should nag until
 * someone answers it"), which means an install nobody has been through cannot produce a single
 * quote and the wizard dead-ends at step 7 with the client told nothing.
 *
 * The client never sees any of this. They are told we are finalising their pricing and left alone;
 * this is the other end of the same fact, put in front of the only person who can act on it.
 */

/** Charge and market as a person would say it. Never the code, except as a last resort. */
function rateName(row: RateRow): string {
	const charge = labelOf(PRICE_CODE_LABELS, row.code);
	return row.market ? `${charge} · ${marketLabel(row.market)}` : charge;
}

export interface BlockedRatesProps {
	/** Every row the screen is showing, charges and carrier together. */
	rows: RateRow[];
	/** Where this is being read, so the copy can say what it means *here*. */
	context: "price-book" | "queue";
	/** A link or button the reader can act on. The price book is already the fix, so it has none. */
	action?: React.ReactNode;
}

/**
 * Amber, named, and counted. Nothing here is a summary — the whole value is naming the exact rows,
 * because "10 rates are not confirmed" sends someone hunting and "Agent setup, Local number · 🇸🇬
 * Singapore, …" sends them to the row.
 */
export function BlockedRates({ rows, context, action }: BlockedRatesProps): React.JSX.Element | null {
	// Rates the server itself refuses to price against — the marker `isPlaceholderRate` matches.
	const blocking = rows.filter((row) => row.placeholder);
	// Rates whose own note says nobody agreed them, worded in a way that marker does not match.
	// They are worse than blocked, not better: they get quoted.
	const quotable = rows.filter((row) => row.unconfirmed && !row.placeholder);
	const missing = rows.filter((row) => row.missing);

	if (blocking.length === 0 && quotable.length === 0 && missing.length === 0) return null;

	const blocked = blocking.length + missing.length;

	return (
		<Alert severity="warning" icon={<WarningIcon weight="fill" />} action={action}>
			<AlertTitle>
				{quotable.length > 0
					? `${quotable.length} rate${quotable.length === 1 ? " is" : "s are"} being quoted without anyone having agreed ${quotable.length === 1 ? "it" : "them"}`
					: `${blocked} rate${blocked === 1 ? " is" : "s are"} stopping every quote that needs ${blocked === 1 ? "it" : "them"}`}
			</AlertTitle>

			{quotable.length > 0 ? (
				<Typography variant="body2">
					They are priced anyway, so a client is sent a figure nobody set. Confirm each at the number that was agreed.
				</Typography>
			) : null}

			{blocked > 0 ? (
				<Typography variant="body2" sx={{ mt: quotable.length > 0 ? 1 : 0 }}>
					{context === "queue"
						? "These are platform rates, so every account's requests sit unpriced until they are confirmed."
						: "Nothing can be quoted against them. Confirm a rate, or set the amount you have agreed."}
				</Typography>
			) : null}

			{/*
			 * Named only where naming them is the whole value. On the admin queue there is no rate
			 * table on the screen, so the chips are the only thing that sends someone to the row —
			 * but on the price book itself they repeat, one for one, the amber rows immediately
			 * below the banner.
			 */}
			{context === "queue" ? (
				<React.Fragment>
					<RateGroup title="Not agreed — still quotable" rows={quotable} variant="soft" />
					<RateGroup title="Not confirmed — blocks quoting" rows={blocking} variant="soft" />
					<RateGroup title="Not set — blocks quoting" rows={missing} variant="outlined" />
				</React.Fragment>
			) : null}
		</Alert>
	);
}

/** One named list of rates. Named, never counted: "10 rates" sends someone hunting. */
function RateGroup({
	title,
	rows,
	variant,
}: {
	title: string;
	rows: RateRow[];
	variant: "soft" | "outlined";
}): React.JSX.Element | null {
	if (rows.length === 0) return null;

	return (
		<Box sx={{ mt: 1 }}>
			<Typography variant="caption" color="text.secondary">
				{title}
			</Typography>
			<Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
				{rows.map((row) => (
					<Chip key={row.key} color="warning" size="small" variant={variant} label={rateName(row)} />
				))}
			</Stack>
		</Box>
	);
}

/**
 * The platform book, resolved into rows, for a screen that is not the price book.
 *
 * Call this only from inside a subtree that has already established the reader is a super admin:
 * `GET /price-book` is cross-account and answers everyone else with a 403, and a hook cannot be
 * called conditionally, so the gate has to be the component boundary rather than an argument.
 * `usePriceBook` does not retry, so the worst a mistake costs is one refused request.
 */
export function usePlatformRateRows(): RateRow[] {
	const { data } = usePriceBook(null);

	return React.useMemo(() => {
		if (!data) return [];
		return [...buildChargeRows(data, null), ...buildCarrierRows(data, null)];
	}, [data]);
}
